import { gql, NetworkStatus, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import {
  ArtistRankingQuery,
  ArtistRankingQueryVariables,
  RankingPeriodBaseConnectionFragmentDoc,
  RankingPeriodType,
  RankingsConnectionFragmentDoc,
  RankingUserWithoutIdConnectionFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { dayjs } from '@/lib/dayjs'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router } = useRouter<{ artistId: string }>()
  const artistId = router.query.artistId

  const { data, refetch, networkStatus, fetchMore } = useQuery<
    ArtistRankingQuery,
    ArtistRankingQueryVariables
  >(QUERY, {
    variables: {
      artistId,
      periodType: RankingPeriodType.Monthly,
      pageInput: { limit: 10 },
    },
    returnPartialData: true,
    notifyOnNetworkStatusChange: true,
  })

  const period = useMemo(
    () => `${dayjs().startOf('month').format('ll')}〜${dayjs().format('ll')}`,
    []
  )

  return {
    state: {
      loading:
        networkStatus === NetworkStatus.loading ||
        networkStatus === NetworkStatus.setVariables,
      refetching: networkStatus === NetworkStatus.fetchMore,
      hasNextPage: false,
      myRanking: data?.artistRankings?.latestRankingPeriod?.myRanking ?? null,
      rankings: data?.artistRankings?.latestRankingPeriod?.rankings ?? null,
      periodType: data?.artistRankings?.latestRankingPeriod?.periodType ?? null,
      period,
      user: data?.user ?? null,
      artist: data?.artistRankings?.artist ?? null,
    },
    actions: {
      async onFetchMore() {},
      async onChangePeriod(periodType: RankingPeriodType) {
        await refetch({ artistId, periodType })
      },
    },
  }
})

const QUERY = gql`
  query ArtistRanking(
    $artistId: String!
    $periodType: RankingPeriodType!
    $pageInput: PageInput
  ) {
    user {
      id
      displayName
      avatar {
        uri
        smallUri
      }
    }
    artistRankings(artistId: $artistId) {
      artist {
        id
        displayName
      }
      latestRankingPeriod(periodType: $periodType) {
        ...RankingPeriodBaseConnection
        myRanking {
          ...RankingUserWithoutIdConnection
        }
        rankings(pageInput: $pageInput) {
          ...RankingsConnection
        }
      }
    }
  }
  ${RankingPeriodBaseConnectionFragmentDoc}
  ${RankingUserWithoutIdConnectionFragmentDoc}
  ${RankingsConnectionFragmentDoc}
`
